<script setup>
import { computed, defineProps, ref, watch } from "vue";

const props = defineProps({
  pickupAddress: { type: String, required: false },
  dropoffAddress: { type: String, required: false },
  returnRequired: { type: Boolean, default: false },
});

const mapCenter = ref({ lat: 53.2487471, lng: -3.0048407 });
const mapZoom = ref(6);
const markers = ref([]);
const routePath = ref([]);
const loading = ref(false);
const error = ref(null);
const distanceMiles = ref(null);
const durationMinutes = ref(null);
let refreshToken = 0;

const normalizeAddress = (value) => String(value ?? "").trim();

const pickup = computed(() => normalizeAddress(props.pickupAddress));
const dropoff = computed(() => normalizeAddress(props.dropoffAddress));
const hasRouteAddresses = computed(
  () => pickup.value !== "" && dropoff.value !== "",
);

const waitForGoogleMaps = async () => {
  if (typeof window !== "undefined" && window.google?.maps) {
    return true;
  }

  for (let attempt = 0; attempt < 20; attempt += 1) {
    await new Promise((resolve) => setTimeout(resolve, 250));
    if (typeof window !== "undefined" && window.google?.maps) {
      return true;
    }
  }

  return false;
};

const toMarkerPosition = (value) => {
  if (!value) return null;

  if (typeof value.lat === "function" && typeof value.lng === "function") {
    return { lat: value.lat(), lng: value.lng() };
  }

  if (
    Number.isFinite(Number(value.lat)) &&
    Number.isFinite(Number(value.lng))
  ) {
    return { lat: Number(value.lat), lng: Number(value.lng) };
  }

  return null;
};

const geocodeAddress = async (address) =>
  new Promise((resolve) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address }, (results, status) => {
      if (status !== "OK" || !results?.length) {
        resolve(null);
        return;
      }

      resolve(toMarkerPosition(results[0]?.geometry?.location));
    });
  });

const requestRoute = async (origin, destination) =>
  new Promise((resolve, reject) => {
    const service = new window.google.maps.DirectionsService();
    service.route(
      {
        origin,
        destination: props.returnRequired ? origin : destination,
        ...(props.returnRequired ? { waypoints: [{ location: destination, stopover: true }] } : {}),
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status !== "OK" || !result?.routes?.length) {
          reject(new Error("Unable to calculate route."));
          return;
        }

        resolve(result);
      },
    );
  });

const updateViewport = (positions) => {
  if (!positions.length) return;

  if (positions.length === 1) {
    mapCenter.value = { ...positions[0] };
    mapZoom.value = 12;
    return;
  }

  const totals = positions.reduce(
    (acc, item) => ({
      lat: acc.lat + item.lat,
      lng: acc.lng + item.lng,
    }),
    { lat: 0, lng: 0 },
  );

  mapCenter.value = {
    lat: totals.lat / positions.length,
    lng: totals.lng / positions.length,
  };
  mapZoom.value = 9;
};

const refreshRoute = async () => {
  const token = ++refreshToken;
  error.value = null;
  routePath.value = [];
  markers.value = [];
  distanceMiles.value = null;
  durationMinutes.value = null;

  if (!hasRouteAddresses.value) {
    return;
  }

  loading.value = true;
  const mapsReady = await waitForGoogleMaps();
  if (token !== refreshToken) return;

  if (!mapsReady) {
    loading.value = false;
    error.value = "Google Maps is unavailable in this session.";
    return;
  }

  try {
    const result = await requestRoute(pickup.value, dropoff.value);
    if (token !== refreshToken) return;

    const route = result.routes?.[0];
    const leg = route?.legs?.[0];
    const start = toMarkerPosition(leg?.start_location);
    const finish = toMarkerPosition(leg?.end_location);
    const path = Array.isArray(route?.overview_path)
      ? route.overview_path.map(toMarkerPosition).filter(Boolean)
      : [];

    markers.value = [start, finish]
      .filter(Boolean)
      .map((position, index) => ({
        id: index === 0 ? "pickup" : "dropoff",
        title: index === 0 ? "Pickup" : "Dropoff",
        position,
      }));
    routePath.value = path;

    const distanceMeters = (route?.legs ?? []).reduce((total, item) => total + Number(item.distance?.value ?? 0), 0);
    const durationSeconds = (route?.legs ?? []).reduce((total, item) => total + Number(item.duration?.value ?? 0), 0);
    distanceMiles.value =
      distanceMeters > 0 ? distanceMeters / 1609.344 : null;
    durationMinutes.value = durationSeconds > 0 ? durationSeconds / 60 : null;

    updateViewport(
      path.length > 1 ? path : markers.value.map((marker) => marker.position),
    );
  } catch (routeError) {
    const [pickupPosition, dropoffPosition] = await Promise.all([
      geocodeAddress(pickup.value),
      geocodeAddress(dropoff.value),
    ]);
    if (token !== refreshToken) return;

    markers.value = [
      pickupPosition
        ? { id: "pickup", title: "Pickup", position: pickupPosition }
        : null,
      dropoffPosition
        ? { id: "dropoff", title: "Dropoff", position: dropoffPosition }
        : null,
    ].filter(Boolean);

    if (pickupPosition && dropoffPosition) {
      routePath.value = [pickupPosition, dropoffPosition];
    }

    updateViewport(markers.value.map((marker) => marker.position));
    error.value =
      "Unable to render a full driving route. Showing available points.";
  } finally {
    if (token === refreshToken) {
      loading.value = false;
    }
  }
};

watch(
  () => [pickup.value, dropoff.value, props.returnRequired],
  () => {
    void refreshRoute();
  },
  { immediate: true },
);
</script>

<template>
  <div class="space-y-2">
    <div v-if="!hasRouteAddresses" class="text-sm text-slate-500">
      Add pickup and dropoff addresses to view the route.
    </div>
    <div v-else-if="loading" class="text-sm text-slate-500">
      Calculating route...
    </div>
    <div
      v-else-if="markers.length"
      class="overflow-hidden rounded-md border border-slate-200 dark:border-slate-700"
    >
      <GMapMap class="h-52 w-full sm:h-64" :center="mapCenter" :zoom="mapZoom">
        <GMapMarker
          v-for="marker in markers"
          :key="marker.id"
          :position="marker.position"
          :title="marker.title"
        />
        <GMapPolyline
          v-if="routePath.length > 1"
          :path="routePath"
          :options="{ strokeColor: '#0f766e', strokeOpacity: 0.9, strokeWeight: 4 }"
        />
      </GMapMap>
    </div>
    <div v-if="distanceMiles || durationMinutes" class="text-xs text-slate-600">
      <span>{{ returnRequired ? 'Patient journey including return' : 'Patient journey' }} (excluding base travel and waiting): </span>
      <span v-if="distanceMiles">
        Distance: {{ Number(distanceMiles).toFixed(1) }} miles
      </span>
      <span v-if="distanceMiles && durationMinutes"> • </span>
      <span v-if="durationMinutes">
        Drive time: {{ Math.round(Number(durationMinutes)) }} mins
      </span>
    </div>
    <div v-if="error" class="text-xs text-amber-600">{{ error }}</div>
  </div>
</template>
